/**
 * CinematicTrackPreview — a 20-second reel-style animated hero shown at the
 * top of each track page. Gives buyers an emotional "this could be me" moment
 * BEFORE they scroll into the prompts.
 *
 * Design intent:
 *   · No actual video required — kinetic typography + animated backdrop drives
 *     a cinematic reel feel using CSS keyframes only. This means it works even
 *     for tracks without a filmed preview.
 *   · If a track ships with `preview_video_url` (YouTube) later, that overrides
 *     the animated version — swap-in is a single line of data.
 *   · Runs a 20-second loop with 4 beats × 5s: kicker → tagline → outcome →
 *     "your turn" CTA. Reduced-motion users get a static poster.
 *
 * Props:
 *   trackNumber:      42 (order)
 *   trackName:        "AI Notion Template Studio · £2K/mo Semi-Passive"
 *   tagline:          "Sell Notion templates on Gumroad → £2K/mo semi-passive..."
 *   iconSlot:         <CategoryIcon /> node — rendered subtly in the backdrop
 *   preview_video_url (optional YouTube 11-char ID)
 *   onScrollToPrompts callback for the CTA button
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { PlayCircle, Sparkles, ArrowDown, Film } from "lucide-react";
import { api } from "../lib/api";

// Split a tagline "Foo bar → £2K/mo baz." into a headline + hero-line so the
// animation can emphasise the £-outcome without regex arithmetic in JSX.
// Falls back to treating the whole tagline as the outcome when no arrow is
// present, so tracks written with em-dashes still get the gradient treatment.
function splitTagline(tagline) {
  if (!tagline) return { setup: "", outcome: "" };
  const parts = tagline.split(/→|-> /);
  if (parts.length >= 2) return { setup: parts[0].trim(), outcome: parts.slice(1).join(" → ").trim() };
  return { setup: "", outcome: tagline.trim() };
}

export function CinematicTrackPreview({
  trackNumber,
  trackName,
  tagline,
  trackSlug = null,
  iconSlot = null,
  previewVideoId = null,
  onScrollToPrompts,
}) {
  const [playing, setPlaying] = useState(true);
  const containerRef = useRef(null);
  const hasEnteredRef = useRef(false);
  const hoverFiredRef = useRef(false);
  const scrollPastFiredRef = useRef(false);
  const { setup, outcome } = splitTagline(tagline);
  // Trim the noun in the track name (e.g. "AI Notion Template Studio · £2K/mo Semi-Passive"
  // → "AI Notion Template Studio") for cleaner kinetic typography.
  const displayTrackName = (trackName || "").split(" · ")[0];

  // Fire a fire-and-forget analytics event. Guards against duplicate fires
  // per component-lifetime via a ref sentinel; no user identity captured.
  const fireEvent = useCallback((event, sentinelRef) => {
    if (sentinelRef && sentinelRef.current) return;
    if (sentinelRef) sentinelRef.current = true;
    if (!trackSlug) return;
    api.post("/analytics/preview-event", {
      track_slug: trackSlug,
      event,
      has_video: !!previewVideoId,
    }).catch(() => { /* silent */ });
  }, [trackSlug, previewVideoId]);

  // Pause the animation loop when the preview scrolls off-screen — saves paint
  // work and stops the loop from being distracting mid-scroll. Also logs
  // scroll_into_view + scroll_past for attention analytics.
  useEffect(() => {
    if (!containerRef.current || typeof IntersectionObserver === "undefined") return;
    const el = containerRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        const inView = entries[0].isIntersecting;
        setPlaying(inView);
        if (inView && !hasEnteredRef.current) {
          hasEnteredRef.current = true;
          fireEvent("scroll_into_view", null);  // fire once when first visible
        } else if (!inView && hasEnteredRef.current && !scrollPastFiredRef.current) {
          scrollPastFiredRef.current = true;
          fireEvent("scroll_past", { current: false });  // fire once when leaving
        }
      },
      { threshold: 0.4 }  // must be 40%+ visible to count as "entered"
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [fireEvent]);

  const handleHover = () => fireEvent("hover", hoverFiredRef);
  const handlePlay = () => fireEvent("play", null);  // fire every embed load — a play intent signal

  // If we've been handed a real YouTube video, render an embed instead of the
  // animated fallback — pure escape hatch for future filmed previews.
  if (previewVideoId) {
    return (
      <section
        className="relative w-full border-b border-purple-500/15 bg-black"
        data-testid="cinematic-track-preview"
        onMouseEnter={handleHover}
        ref={containerRef}
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10 pt-8">
          <div className="relative aspect-video w-full overflow-hidden border border-purple-500/25 shadow-[0_20px_60px_-20px_rgba(168,85,247,0.35)]">
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${previewVideoId}?rel=0&modestbranding=1&playsinline=1`}
              title={`${displayTrackName} — 20-second preview`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              onLoad={handlePlay}
            />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={containerRef}
      onMouseEnter={handleHover}
      className="relative hidden w-full border-b border-purple-500/15 overflow-hidden md:block"
      style={{
        background:
          "radial-gradient(1200px 500px at 20% 0%, rgba(168,85,247,0.18), transparent 60%), radial-gradient(900px 400px at 90% 100%, rgba(176,112,255,0.10), transparent 60%), #050308",
      }}
      data-testid="cinematic-track-preview"
      aria-label={`Preview: ${displayTrackName}`}
    >
      {/* Grain / scanline overlay — pure CSS, no image */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.35) 0px, rgba(255,255,255,0.35) 1px, transparent 1px, transparent 3px)",
        }}
        aria-hidden="true"
      />

      {/* Subtle rotating glow — feels like a reel's light-leak */}
      <div
        className="absolute -top-32 -right-52 w-[420px] h-[420px] md:-top-40 md:-right-40 md:w-[600px] md:h-[600px] pointer-events-none opacity-30"
        style={{
          background: "radial-gradient(circle, rgba(168,85,247,0.4) 0%, transparent 65%)",
          animation: playing ? "cinema-drift 22s ease-in-out infinite" : "none",
        }}
        aria-hidden="true"
      />

      {/* Icon watermark — massive, low-opacity, sits behind the copy */}
      {iconSlot && (
        <div
          className="hidden sm:block absolute right-4 md:right-16 top-1/2 -translate-y-1/2 opacity-[0.06] pointer-events-none"
          style={{ transform: "translateY(-50%) scale(4.5)" }}
          aria-hidden="true"
        >
          {iconSlot}
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-14 md:py-24 relative">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="inline-flex items-center gap-2 text-amber-300 text-[10px] tracking-[0.35em] uppercase font-bold">
            <PlayCircle size={12} className={playing ? "animate-pulse" : ""} />
            20-second preview · Track {String(trackNumber).padStart(2, "0")}
          </div>
          {/* Placeholder poster ribbon — shown only when no filmed reel yet.
              Tells buyers a real 20s walkthrough is on the roadmap so the
              animation doesn't read as the final experience. */}
          <span
            className="inline-flex items-center gap-1.5 border border-purple-500/40 bg-purple-500/[0.08] text-purple-200 px-2.5 py-1 text-[9px] tracking-[0.3em] uppercase"
            data-testid="cinematic-coming-soon-ribbon"
          >
            <Film size={10} /> Filmed reel · Coming soon
          </span>
        </div>

        {/* Beat 1 — track name (fades in first, stays through the loop) */}
        <h2
          className="font-display text-3xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] mb-6 max-w-full break-words"
          style={{ animation: playing ? "cinema-beat1 20s ease-out infinite" : "none" }}
          data-testid="cinematic-track-name"
        >
          {displayTrackName}
        </h2>

        {/* Beat 2 — the setup (context) */}
        <p
          className="text-zinc-300 text-lg md:text-2xl leading-snug max-w-3xl mb-6 font-medium"
          style={{ animation: playing ? "cinema-beat2 20s ease-out infinite" : "none" }}
          data-testid="cinematic-setup-line"
        >
          {setup}
        </p>

        {/* Beat 3 — the outcome (£ number, kinetic emphasis) */}
        {outcome && (
          <div
            className="font-display text-2xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] max-w-full md:max-w-3xl mb-10 break-words"
            style={{ animation: playing ? "cinema-beat3 20s ease-out infinite" : "none" }}
            data-testid="cinematic-outcome-line"
          >
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(135deg, #ddc4ff 0%, #b070ff 45%, #a855f7 100%)" }}
            >
              {setup ? "→ " : ""}{outcome}
            </span>
          </div>
        )}

        {/* Beat 4 — the "your turn" CTA */}
        <div
          className="flex flex-wrap items-center gap-4"
          style={{ animation: playing ? "cinema-beat4 20s ease-out infinite" : "none" }}
        >
          <button
            onClick={onScrollToPrompts}
            className="inline-flex items-center gap-3 bg-purple-500 hover:bg-purple-400 text-black px-6 py-3 text-xs md:text-sm tracking-[0.25em] uppercase font-bold transition-all shadow-[0_0_25px_rgba(168,85,247,0.45)] hover:shadow-[0_0_40px_rgba(168,85,247,0.85)]"
            data-testid="cinematic-cta"
          >
            Run the first prompt <ArrowDown size={15} />
          </button>
          <span className="text-zinc-500 text-xs tracking-wide inline-flex items-center gap-1.5">
            <Sparkles size={12} className="text-amber-300" /> 10 prompts · Copy-paste to any AI
          </span>
        </div>
      </div>

      {/* Keyframes — scoped via a style tag so we don't have to touch global CSS. */}
      <style>{`
        @keyframes cinema-beat1 {
          0%, 100% { opacity: 0; transform: translateY(12px); }
          5%, 95% { opacity: 1; transform: translateY(0); }
        }
        @keyframes cinema-beat2 {
          0%, 20%, 100% { opacity: 0; transform: translateY(10px); }
          25%, 90% { opacity: 1; transform: translateY(0); }
        }
        @keyframes cinema-beat3 {
          0%, 40%, 100% { opacity: 0; transform: translateY(10px) scale(0.98); }
          50%, 90% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes cinema-beat4 {
          0%, 65%, 100% { opacity: 0; transform: translateY(8px); }
          75%, 95% { opacity: 1; transform: translateY(0); }
        }
        @keyframes cinema-drift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-80px, 40px) scale(1.15); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="cinematic-track-preview"] * {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>
    </section>
  );
}
